/**
 * ShopLayout - Shared layout wrapper for all shop pages
 * Includes header with navigation, cart icon, and footer
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { COLORS, FONT_FAMILY, SHOP_COLORS, SHOP_SPACING, GLOBAL_FUN_BUTTON_CSS } from './ShopStyles';
import petjrLogo from '../brand/petjr-logo.png';

const NAV_ITEMS = [
  { id: 'home', label: 'Home', emoji: '🏠' },
  { id: 'gallery', label: 'Inspiration', emoji: '✨' },
  { id: 'products', label: 'Products', emoji: '🛍️' },
  { id: 'about', label: 'About', emoji: '💜' },
];

/** Below this width: hamburger + slide-down menu. Above: full nav (font scales down in steps). */
const SHOP_NAV_HAMBURGER_PX = 900;
/** Logo column tweaks still use this for small phones */
const SHOP_LOGO_TIGHT_PX = 768;

const ShopLayout = ({ children, cartItemCount = 0, onCartClick, onLogoClick, onNavigate, currentPage, isLoggedIn, user, onLoginClick }) => {
  const [hoveredNav, setHoveredNav] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(
    () => (typeof window !== 'undefined' ? window.innerWidth : 1200),
  );
  const isMobile = viewportWidth < SHOP_NAV_HAMBURGER_PX;
  /** Tighter header logo only on smaller phones (nav may already be hamburger) */
  const isCompactLogo = viewportWidth < SHOP_LOGO_TIGHT_PX;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navRef = useRef(null);
  const itemRefs = useRef({});
  /** { left, top, width, height } relative to nav, for sliding rainbow pill */
  const [pillBox, setPillBox] = useState(null);

  const updatePillPosition = useCallback(() => {
    if (typeof window === 'undefined' || isMobile) {
      setPillBox(null);
      return;
    }
    const inNav = NAV_ITEMS.some((n) => n.id === currentPage);
    if (!inNav) {
      setPillBox(null);
      return;
    }
    const nav = navRef.current;
    const btn = itemRefs.current[currentPage];
    if (!nav || !btn) {
      setPillBox(null);
      return;
    }
    const nRect = nav.getBoundingClientRect();
    const bRect = btn.getBoundingClientRect();
    setPillBox({
      left: bRect.left - nRect.left,
      top: bRect.top - nRect.top,
      width: bRect.width,
      height: bRect.height,
    });
  }, [currentPage, isMobile]);

  useLayoutEffect(() => {
    updatePillPosition();
  }, [updatePillPosition, viewportWidth, scrolled]);

  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const ro = new window.ResizeObserver(() => {
      requestAnimationFrame(updatePillPosition);
    });
    ro.observe(nav);
    return () => ro.disconnect();
  }, [updatePillPosition]);
  
  // Track scroll position for logo shrink effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
    };
    
    // Initial check
    handleResize();
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (!isMobile) setMobileMenuOpen(false);
  }, [isMobile]);
  
  // Override body background and inject animations when shop is active
  useEffect(() => {
    const originalBackground = document.body.style.background;
    document.body.style.background = '#fff';
    
    // Inject keyframe animations
    const styleSheet = document.createElement('style');
    styleSheet.id = 'shop-animations';
    // Note: global App.css styles ALL `button` (white text, 50% radius, min-width 180px at ≥600px).
    // The block below plus data attrs keep shop navigation readable and not pill-wrapped.
    styleSheet.textContent = `
      @keyframes floatBlobs {
        0%, 100% { transform: translate(0, 0) scale(1); }
        25% { transform: translate(20px, -30px) scale(1.05); }
        50% { transform: translate(-10px, 20px) scale(0.95); }
        75% { transform: translate(-20px, -10px) scale(1.02); }
      }
      @keyframes bounce {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-8px); }
      }
      @keyframes wiggle {
        0%, 100% { transform: rotate(-2deg); }
        50% { transform: rotate(2deg); }
      }
      @keyframes sparkle {
        0%, 100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.7; transform: scale(1.2); }
      }
      @keyframes shimmer {
        0% { background-position: -200% -200%; }
        100% { background-position: 200% 200%; }
      }
      /* Mobile responsive logo */
      @media (max-width: ${SHOP_LOGO_TIGHT_PX}px) {
        .shop-header-left {
          flex: 0 0 100px !important;
        }
        .shop-header-right {
          flex: 0 0 auto !important;
        }
      }

      /* —— Shop header: open layout, no nested pills; beats global button {} —— */
      .petjr-shop-header .shop-top-nav {
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
        z-index: 0;
        height: 100%;
        min-height: 44px;
        font-family: ${FONT_FAMILY};
        max-width: 100%;
        padding: 0;
        margin: 0;
        background: none;
        border: none;
        box-shadow: none;
        animation: none;
        overflow: visible;
      }
      .petjr-shop-header .shop-nav-pill-mover {
        position: absolute;
        z-index: 0;
        pointer-events: none;
        border: 1.5px solid transparent;
        border-radius: 999px;
        background:
          linear-gradient(180deg, #ffffff 0%, #ffffff 100%) padding-box,
          linear-gradient(120deg, #facc15, #fb7185, #c084fc, #38bdf8, #4ade80, #facc15) border-box;
        -webkit-background-clip: padding-box, border-box;
        background-clip: padding-box, border-box;
        background-size: 100% 100%, 200% 100%;
        background-position: 0 0, 0% 50%;
        box-sizing: border-box;
        will-change: left, top, width, height;
        transition: left 0.38s cubic-bezier(0.25, 0.85, 0.3, 1), top 0.38s cubic-bezier(0.25, 0.85, 0.3, 1), width 0.38s cubic-bezier(0.25, 0.85, 0.3, 1), height 0.38s cubic-bezier(0.25, 0.85, 0.3, 1);
      }
      @media (prefers-reduced-motion: reduce) {
        .petjr-shop-header .shop-nav-pill-mover {
          transition: none !important;
        }
      }
      @media (max-width: ${SHOP_NAV_HAMBURGER_PX - 1}px) {
        .petjr-shop-header .shop-top-nav {
          flex-wrap: wrap;
          column-gap: 12px;
          row-gap: 2px;
        }
      }
      /* Full nav only ≥ hamburger breakpoint (see SHOP_NAV_HAMBURGER_PX) */
      @media (min-width: ${SHOP_NAV_HAMBURGER_PX}px) {
        .petjr-shop-header .shop-top-nav {
          flex-wrap: nowrap;
          column-gap: clamp(20px, 2.5vw, 40px);
        }
        .petjr-shop-header .shop-top-nav .shop-nav-item {
          font-size: 20px;
        }
        .petjr-shop-header .shop-top-nav .shop-nav-item .shop-nav-emoji {
          font-size: 1.5em;
        }
      }
      @media (min-width: ${SHOP_NAV_HAMBURGER_PX}px) and (max-width: 1280px) {
        .petjr-shop-header .shop-top-nav .shop-nav-item {
          font-size: 18px !important;
        }
        .petjr-shop-header .shop-top-nav .shop-nav-item .shop-nav-emoji {
          font-size: 1.38em !important;
        }
      }
      @media (min-width: ${SHOP_NAV_HAMBURGER_PX}px) and (max-width: 1024px) {
        .petjr-shop-header .shop-top-nav .shop-nav-item {
          font-size: 16px !important;
        }
        .petjr-shop-header .shop-top-nav .shop-nav-item .shop-nav-emoji {
          font-size: 1.3em !important;
        }
        .petjr-shop-header .shop-top-nav {
          column-gap: 14px !important;
        }
      }
      .petjr-shop-header .shop-top-nav .shop-nav-item {
        -webkit-appearance: none;
        appearance: none;
        position: relative;
        display: inline-flex !important;
        align-items: center;
        justify-content: center;
        gap: 10px;
        font: inherit;
        font-size: 20px;
        font-weight: 600;
        margin: 0;
        min-width: 0 !important;
        width: auto !important;
        color: #334155;
        background: transparent !important;
        box-shadow: none !important;
        border: none !important;
        border-radius: 0 !important;
        padding: 0 12px !important;
        min-height: 44px !important;
        box-sizing: border-box;
        position: relative;
        z-index: 1;
        cursor: pointer;
        line-height: 1.15;
        letter-spacing: 0.01em;
        transition: color 0.2s ease;
        white-space: nowrap;
      }
      .petjr-shop-header .shop-top-nav .shop-nav-item .shop-nav-emoji {
        font-size: 1.5em;
        line-height: 1;
        display: inline-block;
        transition: transform 0.15s ease;
      }
      .petjr-shop-header .shop-top-nav .shop-nav-item:hover:not([data-active="true"]),
      .petjr-shop-header .shop-top-nav .shop-nav-item:focus-visible:not([data-active="true"]) {
        color: #1e293b;
        outline: none;
        filter: none;
      }
      .petjr-shop-header .shop-top-nav .shop-nav-item:hover:not([data-active="true"]) .shop-nav-emoji {
        transform: scale(1.1) rotate(-4deg);
      }
      /* Active: purple type only — rainbow ring is .shop-nav-pill-mover (slides between items) */
      .petjr-shop-header .shop-top-nav .shop-nav-item[data-active="true"] {
        color: #5b21b6 !important;
        -webkit-text-fill-color: #5b21b6;
        font-weight: 800;
        background: none !important;
        border: none !important;
        box-shadow: none !important;
        text-shadow: none;
        filter: none;
        transform: none;
        padding: 0 12px !important;
        min-height: 44px !important;
      }
      @media (prefers-reduced-motion: reduce) {
        .petjr-shop-header .shop-top-nav .shop-nav-item .shop-nav-emoji { animation: none !important; }
        .petjr-shop-header .shop-top-nav .shop-nav-item { transition: color 0.15s ease !important; }
      }
      .petjr-shop-header .shop-top-nav .shop-nav-item[data-active="true"] .shop-nav-emoji {
        filter: none;
        animation: none;
      }
      .petjr-shop-header .shop-top-nav .shop-nav-item[data-active="true"]:hover,
      .petjr-shop-header .shop-top-nav .shop-nav-item[data-active="true"]:focus-visible {
        color: #4c1d95 !important;
        -webkit-text-fill-color: #4c1d95;
        outline: none;
      }
      .petjr-shop-header .shop-mnav-trigger {
        min-width: 44px !important;
        max-width: 44px;
        width: 44px !important;
        border-radius: 12px !important;
        background: linear-gradient(180deg, #fff 0%, #faf5ff 100%) !important;
        color: #0f172a !important;
        box-shadow: 0 2px 8px rgba(124, 58, 237, 0.12) !important;
        border: 1.5px solid rgba(192, 38, 175, 0.25) !important;
        padding: 0 !important;
        flex-shrink: 0;
        transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
      }
      .petjr-shop-header .shop-mnav-trigger:hover {
        border-color: rgba(124, 58, 237, 0.5) !important;
        box-shadow: 0 3px 12px rgba(234, 88, 12, 0.15) !important;
        transform: scale(1.05) !important;
      }
      .petjr-shop-header .shop-header-action {
        min-width: 0 !important;
        width: auto !important;
      }
      .petjr-shop-mnav-panel button.shop-mnav-row,
      .petjr-shop-mnav-backdrop {
        -webkit-appearance: none;
        appearance: none;
        min-width: 0 !important;
        border-radius: 0 !important;
        font: inherit;
      }
      .petjr-shop-mnav-panel .shop-mnav-emoji {
        font-size: 1.5em;
        line-height: 1;
        flex-shrink: 0;
      }
      .petjr-shop-mnav-backdrop {
        color: transparent !important;
        box-shadow: none !important;
        border: none !important;
      }
      
      /* Footer responsive styles */
      @media (max-width: 900px) {
        .footer-grid {
          grid-template-columns: 1fr !important;
          gap: 32px !important;
          text-align: center !important;
        }
        .footer-links-section {
          text-align: center !important;
        }
        .footer-links-grid {
          align-items: center !important;
        }
        .footer-link {
          justify-content: center !important;
        }
      }
    
    ${GLOBAL_FUN_BUTTON_CSS}
    `;
    document.head.appendChild(styleSheet);
    
    return () => {
      document.body.style.background = originalBackground;
      const existingStyle = document.getElementById('shop-animations');
      if (existingStyle) existingStyle.remove();
    };
  }, []);
  
  // Dynamic logo sizes — logo is position:absolute so it must NOT use marginTop (was inflating header ~2×).
  const logoSize = scrolled ? (isCompactLogo ? 48 : 64) : (isCompactLogo ? 76 : 118);
  const logoPadding = scrolled ? '20% 12% 12%' : '30% 17% 17%';
  const logoTop = scrolled ? 4 : (isCompactLogo ? 6 : 4);

  return (
    <div style={styles.container}>
      {/* Background layers */}
      <div style={styles.patternLayer} />
      <div style={styles.gradientOverlay} />
      <div style={styles.accentGlows} />
      
      {/* Header */}
      <header className="petjr-shop-header" style={styles.header}>
        <div style={styles.headerContainer}>
          {/* Left: Logo - shrinks on scroll */}
          <div style={styles.headerLeft}>
            <div
              style={{
                ...styles.logoContainer,
                top: logoTop,
              }}
              onClick={onLogoClick}
            >
              <div style={{
                ...styles.logoCircle,
                width: logoSize,
                height: logoSize,
                padding: logoPadding,
              }}>
                <img 
                  src={petjrLogo} 
                  alt="PetJr" 
                  style={styles.logoImage}
                />
              </div>
            </div>
          </div>
          
          {/* Center: full nav at/above SHOP_NAV_HAMBURGER_PX; hamburger below */}
          <div style={styles.headerCenter}>
            {!isMobile ? (
              <nav
                ref={navRef}
                className="shop-top-nav"
                style={styles.navBar}
                aria-label="Shop"
              >
                {pillBox && (
                  <div
                    className="shop-nav-pill-mover"
                    style={{
                      left: pillBox.left,
                      top: pillBox.top,
                      width: pillBox.width,
                      height: pillBox.height,
                    }}
                    aria-hidden
                  />
                )}
                {NAV_ITEMS.map((item) => {
                  const active = currentPage === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      className="shop-nav-item"
                      data-active={active ? 'true' : 'false'}
                      ref={(el) => {
                        if (el) itemRefs.current[item.id] = el;
                        else delete itemRefs.current[item.id];
                      }}
                      onClick={() => onNavigate?.(item.id)}
                    >
                      <span className="shop-nav-emoji" aria-hidden>
                        {item.emoji}
                      </span>
                      <span className="shop-nav-label">{item.label}</span>
                    </button>
                  );
                })}
              </nav>
            ) : (
              <button
                type="button"
                className="shop-mnav-trigger"
                style={{
                  ...styles.mobileMenuButton,
                  ...(mobileMenuOpen ? styles.mobileMenuButtonOpen : {}),
                }}
                aria-expanded={mobileMenuOpen}
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                onClick={() => setMobileMenuOpen((o) => !o)}
              >
                <span style={styles.mobileMenuIconLine} />
                <span style={styles.mobileMenuIconLine} />
                <span style={styles.mobileMenuIconLine} />
              </button>
            )}
          </div>

          {/* Right: Account & Cart */}
          <div style={styles.headerRight} className="shop-header-actions">
            {isLoggedIn ? (
              <button
                type="button"
                className="shop-header-action"
                style={{
                  ...styles.accountBtn,
                  color: '#374151',
                  borderColor: hoveredNav === 'account' ? 'rgba(147, 51, 234, 0.3)' : 'rgba(147, 51, 234, 0.15)',
                  boxShadow: hoveredNav === 'account' ? '0 4px 12px rgba(147, 51, 234, 0.15)' : '0 2px 8px rgba(147, 51, 234, 0.08)',
                }}
                onMouseEnter={() => setHoveredNav('account')}
                onMouseLeave={() => setHoveredNav(null)}
                onClick={() => onNavigate?.('account')}
              >
                <span style={styles.accountEmoji}>{user?.avatar || '👤'}</span>
                <span style={styles.accountName}>{user?.name?.split(' ')[0] || 'Account'}</span>
              </button>
            ) : (
              <button
                type="button"
                className="shop-header-action"
                style={{
                  ...styles.signInBtn,
                  color: '#ffffff',
                  background: hoveredNav === 'account' 
                    ? 'linear-gradient(135deg, #7c3aed 0%, #9333ea 100%)' 
                    : 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)',
                  boxShadow: hoveredNav === 'account' ? '0 6px 20px rgba(147, 51, 234, 0.35)' : '0 2px 8px rgba(147, 51, 234, 0.2)',
                }}
                onMouseEnter={() => setHoveredNav('account')}
                onMouseLeave={() => setHoveredNav(null)}
                onClick={onLoginClick}
              >
                <span style={styles.signInEmoji}>👤</span>
                <span style={styles.signInText}>Sign In</span>
              </button>
            )}
            <button
              type="button"
              className="shop-header-action"
              style={{
                ...styles.cartButton,
                color: '#374151',
                transform: hoveredNav === 'cart' ? 'translateY(-2px)' : 'translateY(0)',
                borderColor: hoveredNav === 'cart' ? 'rgba(147, 51, 234, 0.3)' : 'rgba(147, 51, 234, 0.15)',
                boxShadow: hoveredNav === 'cart' ? '0 4px 12px rgba(147, 51, 234, 0.15)' : '0 2px 8px rgba(147, 51, 234, 0.08)',
              }}
              onMouseEnter={() => setHoveredNav('cart')}
              onMouseLeave={() => setHoveredNav(null)}
              onClick={onCartClick}
            >
              <span style={styles.cartIcon}>🛒</span>
              <span style={styles.cartText}>Cart</span>
              {cartItemCount > 0 && (
                <span style={styles.cartBadge}>{cartItemCount}</span>
              )}
            </button>
          </div>
        </div>
      </header>

      {isMobile && mobileMenuOpen && (
        <>
          <button
            type="button"
            className="petjr-shop-mnav-backdrop"
            style={styles.mobileNavBackdrop}
            aria-label="Close menu"
            onClick={() => setMobileMenuOpen(false)}
          />
          <nav className="petjr-shop-mnav-panel" style={styles.mobileNavPanel} aria-label="Shop">
            {NAV_ITEMS.map((item) => {
              const active = currentPage === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  className="shop-mnav-row"
                  style={{
                    ...styles.mobileNavRow,
                    ...(active ? styles.mobileNavRowActive : {}),
                  }}
                  onClick={() => {
                    onNavigate?.(item.id);
                    setMobileMenuOpen(false);
                  }}
                >
                  <span className="shop-mnav-emoji" aria-hidden>
                    {item.emoji}
                  </span>
                  {item.label}
                </button>
              );
            })}
          </nav>
        </>
      )}

      {/* Main Content */}
      <main style={styles.main}>
        {children}
      </main>

      {/* Footer */}
      <footer style={styles.footer}>
        {/* Wavy top decoration */}
        <div style={styles.footerWave}>
          <svg viewBox="0 0 1440 120" preserveAspectRatio="none" style={{ width: '100%', height: '100%', display: 'block' }}>
            <path 
              d="M0,64 C288,120 576,0 864,64 C1152,128 1296,32 1440,64 L1440,120 L0,120 Z" 
              fill="url(#footerGradient)"
            />
            <defs>
              <linearGradient id="footerGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#fef3c7" />
                <stop offset="25%" stopColor="#ede9fe" />
                <stop offset="50%" stopColor="#fce7f3" />
                <stop offset="75%" stopColor="#e0f2fe" />
                <stop offset="100%" stopColor="#fef3c7" />
              </linearGradient>
            </defs>
          </svg>
          {/* Floating paw prints on the wave */}
          <span style={{ ...styles.floatingPaw, left: '8%', animationDelay: '0s' }}>🐾</span>
          <span style={{ ...styles.floatingPaw, left: '25%', animationDelay: '0.5s' }}>✨</span>
          <span style={{ ...styles.floatingPaw, left: '45%', animationDelay: '1s' }}>🐕</span>
          <span style={{ ...styles.floatingPaw, left: '65%', animationDelay: '1.5s' }}>💜</span>
          <span style={{ ...styles.floatingPaw, left: '82%', animationDelay: '2s' }}>🐈</span>
        </div>
        
        <div style={styles.footerContent}>
          {/* Main footer grid */}
          <div style={styles.footerGrid} className="footer-grid">
            {/* Left: Made by Zoe feature card */}
            <div style={styles.footerMadeByCard}>
              <div style={styles.madeByHeader}>
                <span style={styles.madeBySparkle}>✨</span>
                <span style={styles.madeByTitle}>Meet the Creator</span>
                <span style={styles.madeBySparkle}>✨</span>
              </div>
              <button 
                style={styles.footerMadeBy}
                onClick={() => onNavigate?.('about')}
                onMouseEnter={(e) => { 
                  e.currentTarget.style.transform = 'translateY(-2px) scale(1.01)'; 
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(251, 191, 36, 0.25)';
                  e.currentTarget.querySelector('.made-by-arrow').style.transform = 'translateX(4px)';
                }}
                onMouseLeave={(e) => { 
                  e.currentTarget.style.transform = 'translateY(0) scale(1)'; 
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(251, 191, 36, 0.1)';
                  e.currentTarget.querySelector('.made-by-arrow').style.transform = 'translateX(0)';
                }}
              >
                <div style={styles.madeByAvatar}>👩🏻‍🦱</div>
                <div style={styles.madeByInfo}>
                  <span style={styles.madeByName}>Zoe, age 9</span>
                  <span style={styles.madeByRole}>Designer & Dreamer</span>
                </div>
                <div style={styles.madeByArrow} className="made-by-arrow">→</div>
              </button>
              <p style={styles.madeByTagline}>
                Building something pawsome with Dad! 💪
              </p>
            </div>
            
            {/* Center: Brand & Tagline */}
            <div style={styles.footerBrand}>
              <div style={styles.footerLogoWrapper}>
                <img 
                  src={petjrLogo} 
                  alt="PetJr" 
                  style={styles.footerLogo}
                />
              </div>
              <p style={styles.footerTagline}>
                Turn your pet into art,<br />then into pawsome products!
              </p>
              <div style={styles.footerPets}>
                <span style={{ ...styles.footerPet, animationDelay: '0s' }}>🐕</span>
                <span style={{ ...styles.footerPet, animationDelay: '0.2s' }}>🐈</span>
                <span style={{ ...styles.footerPet, animationDelay: '0.4s' }}>🐰</span>
                <span style={{ ...styles.footerPet, animationDelay: '0.6s' }}>🐹</span>
              </div>
            </div>
            
            {/* Right: Quick Links */}
            <div style={styles.footerLinksSection} className="footer-links-section">
              <h4 style={styles.footerLinksTitle}>Quick Links</h4>
              <div style={styles.footerLinksGrid} className="footer-links-grid">
                <a 
                  href="#" 
                  className="footer-link"
                  style={styles.footerLink} 
                  onClick={(e) => { e.preventDefault(); onLogoClick?.(); }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(126, 82, 199, 0.12)'; e.currentTarget.style.color = '#7E52C7'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(126, 82, 199, 0.05)'; e.currentTarget.style.color = '#555'; }}
                >
                  <span style={styles.linkIcon}>🎨</span>
                  <span>Original Creator</span>
                </a>
                <a 
                  href="#" 
                  className="footer-link"
                  style={styles.footerLink} 
                  onClick={(e) => e.preventDefault()}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(126, 82, 199, 0.12)'; e.currentTarget.style.color = '#7E52C7'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(126, 82, 199, 0.05)'; e.currentTarget.style.color = '#555'; }}
                >
                  <span style={styles.linkIcon}>❓</span>
                  <span>Help & FAQ</span>
                </a>
                <a 
                  href="#" 
                  className="footer-link"
                  style={styles.footerLink} 
                  onClick={(e) => e.preventDefault()}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(126, 82, 199, 0.12)'; e.currentTarget.style.color = '#7E52C7'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(126, 82, 199, 0.05)'; e.currentTarget.style.color = '#555'; }}
                >
                  <span style={styles.linkIcon}>📜</span>
                  <span>Terms</span>
                </a>
                <a 
                  href="#" 
                  className="footer-link"
                  style={styles.footerLink} 
                  onClick={(e) => e.preventDefault()}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(126, 82, 199, 0.12)'; e.currentTarget.style.color = '#7E52C7'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(126, 82, 199, 0.05)'; e.currentTarget.style.color = '#555'; }}
                >
                  <span style={styles.linkIcon}>🔒</span>
                  <span>Privacy</span>
                </a>
              </div>
            </div>
          </div>
          
          {/* Bottom bar with copyright */}
          <div style={styles.footerBottom}>
            <div style={styles.footerDividerLine} />
            <p style={styles.footerCopyright}>
              © {new Date().getFullYear()} PetJr • Made with 💜 in Boston
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};


const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',
    background: '#fff', // White base
  },
  // Fun pet doodle pattern background - tiled
  patternLayer: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
    backgroundImage: 'url(/assets/site-bg.png)',
    backgroundSize: '450px',
    backgroundPosition: 'center',
    backgroundRepeat: 'repeat',
    opacity: 0.35, // More visible!
    pointerEvents: 'none',
  },
  // Softer center gradient - lets more pattern show at edges
  gradientOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1,
    background: `
      radial-gradient(ellipse 80% 70% at 50% 45%, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 40%, rgba(255,255,255,0.4) 100%)
    `,
    pointerEvents: 'none',
  },
  // Fun colorful blobs that animate
  accentGlows: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 2,
    background: `
      radial-gradient(circle 400px at 10% 20%, rgba(255, 107, 157, 0.15) 0%, transparent 50%),
      radial-gradient(circle 300px at 90% 80%, rgba(78, 205, 196, 0.15) 0%, transparent 50%),
      radial-gradient(circle 350px at 80% 10%, rgba(255, 195, 113, 0.12) 0%, transparent 50%),
      radial-gradient(circle 250px at 20% 90%, rgba(147, 51, 234, 0.12) 0%, transparent 50%)
    `,
    pointerEvents: 'none',
    animation: 'floatBlobs 20s ease-in-out infinite',
  },
  header: {
    background: 'rgba(255,255,255,0.95)',
    backdropFilter: 'blur(12px)',
    borderBottom: '1px solid rgba(0,0,0,0.06)',
    position: 'sticky',
    top: 0,
    zIndex: 100,
    boxShadow: '0 2px 20px rgba(0,0,0,0.04)',
    height: '56px',
    minHeight: '56px',
    maxHeight: '56px',
    overflow: 'visible',
  },
  headerContainer: {
    maxWidth: '1600px',
    margin: '0 auto',
    padding: `0 ${SHOP_SPACING.lg}`,
    height: '56px',
    minHeight: '56px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'relative',
  },
  headerLeft: {
    flex: '0 0 160px',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    alignSelf: 'stretch',
    minHeight: '56px',
    overflow: 'visible',
  },
  headerCenter: {
    flex: '1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'stretch',
    minWidth: 0,
    height: '100%',
  },
  headerRight: {
    flex: '0 0 220px',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    alignSelf: 'stretch',
    height: '100%',
    gap: '10px',
  },
  accountButton: {
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.9)',
    border: '2px solid rgba(147, 51, 234, 0.15)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
    boxShadow: '0 2px 8px rgba(147, 51, 234, 0.08)',
  },
  accountButtonLoggedIn: {
    background: 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)',
    border: 'none',
    boxShadow: '0 4px 12px rgba(147, 51, 234, 0.3)',
  },
  accountIcon: {
    fontSize: '20px',
  },
  loginButton: {
    padding: '10px 18px',
    fontSize: '14px',
    fontWeight: '600',
    fontFamily: 'inherit',
    background: 'rgba(255,255,255,0.9)',
    border: '2px solid rgba(147, 51, 234, 0.2)',
    borderRadius: '50px',
    color: '#7c3aed',
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
    boxShadow: '0 2px 8px rgba(147, 51, 234, 0.08)',
  },
  // Compact account button (logged in)
  accountBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
    border: '2px solid rgba(124, 58, 237, 0.22)',
    borderRadius: '16px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    minHeight: '44px',
    boxShadow: '0 2px 6px rgba(15, 23, 42, 0.06)',
  },
  accountEmoji: {
    fontSize: '20px',
    lineHeight: 1,
  },
  accountName: {
    fontSize: '15px',
    fontWeight: '700',
    color: '#1e293b',
    fontFamily: FONT_FAMILY,
  },
  // Sign in (logged out)
  signInBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 18px',
    background: 'linear-gradient(135deg, #9d3aed 0%, #7c3aed 100%)',
    border: 'none',
    borderRadius: '16px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    minHeight: '44px',
    boxShadow: '0 2px 10px rgba(124, 58, 237, 0.3)',
  },
  signInEmoji: {
    fontSize: '18px',
  },
  signInText: {
    fontSize: '15px',
    fontWeight: '700',
    color: '#fff',
    fontFamily: FONT_FAMILY,
  },
  logoContainer: {
    cursor: 'pointer',
    zIndex: 101,
    position: 'absolute',
    left: 0,
    top: 4,
    margin: 0,
    transition: 'all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)',
  },
  logoCircle: {
    borderRadius: '50%',
    background: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)',
  },
  logoImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    transition: 'all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)',
  },
  navBar: {
    position: 'relative',
    zIndex: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    minHeight: '44px',
  },
  mobileMenuButton: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '5px',
    width: '44px',
    height: '44px',
    padding: 0,
    background: 'rgba(255,255,255,0.95)',
    border: '1px solid rgba(147, 51, 234, 0.2)',
    borderRadius: '10px',
    cursor: 'pointer',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  },
  mobileMenuButtonOpen: {
    borderColor: '#7c3aed',
    background: 'rgba(124, 58, 237, 0.06)',
  },
  mobileMenuIconLine: {
    display: 'block',
    width: '20px',
    height: '2px',
    background: 'linear-gradient(90deg, #7c3aed, #e879f9, #fbbf24)',
    borderRadius: '2px',
  },
  mobileNavBackdrop: {
    position: 'fixed',
    top: '56px',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 90,
    border: 'none',
    padding: 0,
    margin: 0,
    cursor: 'default',
    background: 'rgba(15, 23, 42, 0.35)',
  },
  mobileNavPanel: {
    position: 'fixed',
    top: '56px',
    left: 0,
    right: 0,
    zIndex: 91,
    background: 'linear-gradient(180deg, #fff 0%, #fffef9 55%, #fef3c7 100%)',
    borderBottom: '1px solid rgba(168, 85, 250, 0.2)',
    boxShadow: '0 16px 40px rgba(124, 58, 237, 0.15)',
    display: 'flex',
    flexDirection: 'column',
    padding: '8px 0 12px',
  },
  mobileNavRow: {
    width: '100%',
    textAlign: 'left',
    border: 'none',
    background: 'transparent',
    color: '#374151',
    fontSize: '18px',
    fontWeight: '600',
    fontFamily: FONT_FAMILY,
    padding: '16px 20px',
    cursor: 'pointer',
    borderLeft: '4px solid transparent',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  mobileNavRowActive: {
    color: '#5b21b6',
    background: 'linear-gradient(90deg, rgba(192, 38, 175, 0.1) 0%, rgba(255, 251, 235, 0.4) 100%)',
    borderLeftColor: '#d946ef',
  },
  cartButton: {
    background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
    border: '2px solid rgba(124, 58, 237, 0.22)',
    borderRadius: '16px',
    padding: '8px 16px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    position: 'relative',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 6px rgba(15, 23, 42, 0.06)',
    minHeight: '44px',
  },
  cartIcon: {
    fontSize: '20px',
    lineHeight: 1,
  },
  cartText: {
    fontSize: '15px',
    fontWeight: '700',
    color: '#1e293b',
    fontFamily: FONT_FAMILY,
  },
  cartBadge: {
    background: 'linear-gradient(135deg, #9333ea 0%, #ec4899 100%)',
    color: '#fff',
    fontSize: '10px',
    fontWeight: '700',
    padding: '1px 6px',
    borderRadius: '8px',
    minWidth: '18px',
    textAlign: 'center',
    fontFamily: FONT_FAMILY,
  },
  main: {
    flex: 1,
    maxWidth: '1600px',
    width: '100%',
    margin: '0 auto',
    padding: `${SHOP_SPACING.lg} ${SHOP_SPACING.xl}`,
    position: 'relative',
    zIndex: 10,
  },
  footer: {
    background: 'linear-gradient(180deg, #fef7ed 0%, #faf5ff 50%, #f0f9ff 100%)',
    marginTop: 'auto',
    position: 'relative',
    zIndex: 10,
    overflow: 'hidden',
  },
  footerWave: {
    position: 'relative',
    height: '80px',
    marginBottom: '-2px',
  },
  floatingPaw: {
    position: 'absolute',
    top: '20%',
    fontSize: '24px',
    animation: 'bounce 2s ease-in-out infinite',
    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
  },
  footerContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: `${SHOP_SPACING.xl} ${SHOP_SPACING.lg} ${SHOP_SPACING.lg}`,
  },
  footerGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '40px',
    alignItems: 'start',
    '@media (max-width: 900px)': {
      gridTemplateColumns: '1fr',
      gap: '32px',
    },
  },
  footerMadeByCard: {
    background: 'linear-gradient(135deg, #fff 0%, #fef3c7 100%)',
    borderRadius: '24px',
    padding: '24px',
    boxShadow: '0 8px 32px rgba(251, 191, 36, 0.15)',
    border: '2px solid rgba(251, 191, 36, 0.2)',
    textAlign: 'center',
  },
  madeByHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    marginBottom: '10px',
  },
  madeBySparkle: {
    fontSize: '18px',
    animation: 'sparkle 2s ease-in-out infinite',
  },
  madeByTitle: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#92400e',
    fontFamily: FONT_FAMILY,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  footerMadeBy: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
    padding: '8px 12px',
    background: 'linear-gradient(135deg, #fff 0%, #fffbeb 100%)',
    borderRadius: '14px',
    border: '2px solid rgba(251, 191, 36, 0.3)',
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
    boxShadow: '0 4px 12px rgba(251, 191, 36, 0.1)',
  },
  madeByAvatar: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #fde68a 0%, #fbbf24 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    boxShadow: '0 4px 12px rgba(251, 191, 36, 0.3)',
  },
  madeByInfo: {
    flex: 1,
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  madeByName: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#92400e',
    fontFamily: FONT_FAMILY,
  },
  madeByRole: {
    fontSize: '11px',
    fontWeight: '500',
    color: '#b45309',
    fontFamily: FONT_FAMILY,
  },
  madeByArrow: {
    fontSize: '16px',
    color: '#fbbf24',
    transition: 'transform 0.3s ease',
  },
  madeByTagline: {
    marginTop: '12px',
    fontSize: '13px',
    color: '#92400e',
    fontFamily: FONT_FAMILY,
    fontWeight: '500',
  },
  footerBrand: {
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  footerLogoWrapper: {
    width: '100px',
    height: '100px',
    borderRadius: '50%',
    background: '#fff',
    padding: '20px',
    boxShadow: '0 8px 32px rgba(126, 82, 199, 0.15)',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  footerLogo: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  footerTagline: {
    color: '#666',
    fontSize: '16px',
    fontFamily: FONT_FAMILY,
    fontWeight: '500',
    margin: 0,
    marginBottom: '16px',
    lineHeight: 1.5,
  },
  footerPets: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'center',
  },
  footerPet: {
    fontSize: '28px',
    animation: 'wiggle 3s ease-in-out infinite',
    cursor: 'default',
    transition: 'transform 0.2s ease',
  },
  footerLinksSection: {
    textAlign: 'left',
  },
  footerLinksTitle: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#7E52C7',
    fontFamily: FONT_FAMILY,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    margin: 0,
    marginBottom: '16px',
  },
  footerLinksGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  footerLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 14px',
    background: 'rgba(126, 82, 199, 0.05)',
    borderRadius: '12px',
    textDecoration: 'none',
    transition: 'all 0.2s ease',
    color: '#555',
    fontSize: '14px',
    fontWeight: '500',
    fontFamily: FONT_FAMILY,
  },
  linkIcon: {
    fontSize: '16px',
  },
  footerBottom: {
    marginTop: '40px',
    textAlign: 'center',
  },
  footerDividerLine: {
    height: '2px',
    background: 'linear-gradient(90deg, transparent, rgba(126, 82, 199, 0.2), transparent)',
    marginBottom: '20px',
  },
  footerCopyright: {
    color: '#999',
    fontSize: '13px',
    fontFamily: FONT_FAMILY,
    margin: 0,
  },
};

export default ShopLayout;

